/**
 * ThreadHeaderOverflowMenu — the docked header's "⋯" overflow (docked
 * density round, Miles 2026-08-01).
 *
 * At docked width (~420px pane) the header icon rail was five buttons wide
 * (pin, archive, add-people, mode-cycle, hide) beside an ellipsizing title —
 * the owner's screenshot showed it cramming the top of the panel. In docked
 * mode the rail keeps THREE invariant buttons max: this ⋯ trigger +
 * mode-cycle (expand) + hide (minimize). Everything secondary folds into
 * this body-portaled popover:
 *   • pin / unpin the active thread    (SAME threadStore.togglePinThread)
 *   • archive the active thread       (SAME handleHeaderArchiveActiveThread
 *     row-×-parity flow; hidden for the last remaining thread)
 *   • add people                      (flag-gated, SAME threadAddPeople.open)
 *   • new thread                      (keeps creation reachable while the
 *     docked strip hides under 2 threads — see dockedDensity.ts)
 *   • clear chat history              (only when the rail button would show,
 *     i.e. threads <= 1 — PR #68's accidental-wipe rule is unchanged)
 *
 * Presentational + one bounded popover: every action is a host callback, so
 * the docked rail can never diverge from the wide rail's behavior. Portal /
 * keyboard conventions mirror SidebarAccountMenuSection (ThreadSidebar):
 * body portal at BODY_PORTAL_Z_INDEX, fixed viewport-clamped coordinates,
 * focus-first-item on open, Escape closes + refocuses the trigger, outside
 * pointer-down closes. It opens DOWNWARD from the top-right rail.
 * `data-header-overflow-btn` / `data-header-overflow-menu` are the stable
 * hooks.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HeaderIconButton, HeaderOverflowMenu, HeaderOverflowMenuItem, ThreadUnreadBadge } from './styles';
import { CategoryFolderGlyph } from './ThreadTypeGlyph';
import { CategoryMoveList } from './CategoryMoveMenu';
import type { ThreadCategory, ThreadListLang } from '../../types/threadCategories';
import {
  MoreSvgIcon,
  PinSvgIcon,
  ArchiveBoxSvgIcon,
  AddPeopleSvgIcon,
  MentionOffSvgIcon,
  MentionSvgIcon,
  PlusSvgIcon,
  TrashSvgIcon,
} from './components';

export interface ThreadHeaderOverflowMenuProps {
  /** Active thread id — null on config-driven surfaces with no thread. */
  threadId: string | null;
  /** Display name for aria-labels (routine name / thread title). */
  threadTitle: string;
  /** Current pinned state of the active thread. */
  pinned: boolean;
  /** The SAME togglePinThread the wide rail / row / chip pins call. */
  onTogglePin: (threadId: string) => void;
  /** Row-×-parity: false (last remaining thread) hides the archive item. */
  canArchive: boolean;
  /** The SAME handleHeaderArchiveActiveThread flow as the wide rail. */
  onRequestArchive: (threadId: string) => void;
  /** Add-people trigger (flag-gated testing-only, same as the wide rail). */
  addPeopleEnabled: boolean;
  onAddPeople: () => void;
  /**
   * May this viewer change who is in the thread (2026-09-08)? Absent ⇒ true.
   * False relabels the people item ("people" — look, not change) and hides
   * the Prest0n item; the panel itself shows the refusal line.
   */
  canManagePeople?: boolean;
  /** Show the Prest0n in/out item (a shared thread with the manage authority). */
  assistantToggleEnabled?: boolean;
  /** Is Prest0n in this thread? Absent ⇒ true. */
  assistantEnabled?: boolean;
  onToggleAssistant?: (next: boolean) => void;
  /**
   * W3b: show the "@Prest0n required to reply" toggle. True only when the
   * account flag is on AND this thread has two or more humans — the setting
   * gates nothing in a one-on-one, so offering it there would only confuse.
   */
  summonToggleEnabled?: boolean;
  /** Current state of that toggle (true = a mention is required). */
  summonRequireMention?: boolean;
  onToggleSummonRequirement?: (next: boolean) => void;
  /**
   * Start a new thread. Present whenever the thread system is on — the
   * docked strip hides itself under 2 threads (dockedDensity.ts), so this
   * item is what keeps creation reachable at every docked state.
   */
  onNewThread?: () => void;
  /** Mirror of the wide rail's showTopLevelClearButton gate. */
  showClear: boolean;
  clearLabel: string;
  clearAriaLabel: string;
  onClear: () => void;
  /**
   * Per-thread badges (2026-09-08): OTHER threads carry unseen activity
   * (never the open one) — the docked ⋯ wears the same dot the wide rail's
   * bell wears, since the docked rail has no bell.
   */
  otherThreadsUnseen?: boolean;
  /**
   * PERSONAL ORGANIZATION (PR-5, 2026-09-09): "move to…" — the viewer's own
   * categories, the one this thread is filed under, and the handlers. The
   * item renders only when `onMoveToCategory` is given (a thread is open).
   */
  categories?: readonly ThreadCategory[] | null;
  currentCategoryId?: string | null;
  onMoveToCategory?: (categoryId: string | null) => void;
  /** Create a category by name; resolves its id (null = refused/empty). */
  onCreateCategory?: (name: string) => Promise<string | null>;
  /** The built-in an unfiled thread falls into (reads as checked when nothing is filed). */
  naturalSectionId?: string | null;
  lang?: ThreadListLang;
}

export const ThreadHeaderOverflowMenu: React.FC<ThreadHeaderOverflowMenuProps> = ({
  threadId,
  threadTitle,
  pinned,
  onTogglePin,
  canArchive,
  onRequestArchive,
  addPeopleEnabled,
  onAddPeople,
  canManagePeople,
  assistantToggleEnabled,
  assistantEnabled,
  onToggleAssistant,
  summonToggleEnabled,
  summonRequireMention,
  onToggleSummonRequirement,
  onNewThread,
  showClear,
  clearLabel,
  clearAriaLabel,
  onClear,
  otherThreadsUnseen,
  categories,
  currentCategoryId,
  onMoveToCategory,
  onCreateCategory,
  naturalSectionId,
  lang,
}) => {
  const [open, setOpen] = useState(false);
  // PR-5: the "move to…" second level — the same menu surface swaps its
  // body for the category list (no nested popovers on a phone).
  const [moveToOpen, setMoveToOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 8, top: 56 });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const close = useCallback((refocusButton: boolean) => {
    setOpen(false);
    setMoveToOpen(false);
    if (refocusButton) buttonRef.current?.focus();
  }, []);

  const toggle = useCallback(() => {
    if (open) {
      close(false);
      return;
    }
    // Anchor BELOW the trigger, right-aligned to it (the rail hugs the
    // pane's right edge), clamped to the viewport — mirrors
    // SidebarAccountMenuSection's fixed-coordinate approach.
    const rect = buttonRef.current?.getBoundingClientRect();
    if (rect && typeof window !== 'undefined') {
      const menuWidth = 192; // min-width 184 + padding allowance
      setPos({
        left: Math.max(8, Math.min(rect.right - menuWidth, (window.innerWidth || menuWidth) - menuWidth - 8)),
        top: Math.max(8, rect.bottom + 6),
      });
    }
    setOpen(true);
  }, [open, close]);

  // Focus the first menu item on open so the body-portaled menu is
  // reachable by keyboard (same as the sidebar account menu).
  useEffect(() => {
    if (!open) return;
    menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  // Escape closes + refocuses the trigger; outside pointer-down closes.
  // Document-level listeners because the menu lives in a body portal.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close(true);
    };
    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return;
      close(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
    };
  }, [open, close]);

  const pick = useCallback((action: () => void) => () => {
    close(false);
    action();
  }, [close]);

  const showPin = !!threadId;
  const showArchive = !!threadId && canArchive;
  const showNewThread = !!onNewThread;
  const showMoveTo = !!threadId && !!onMoveToCategory;
  // Nothing to fold away → no trigger at all (e.g. a config-driven surface
  // with no thread, threads off, and no clear state).
  if (!showPin && !showArchive && !addPeopleEnabled && !summonToggleEnabled && !showNewThread && !showClear) {
    return null;
  }

  return (
    <>
      <HeaderIconButton
        ref={buttonRef}
        data-header-overflow-btn=""
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={otherThreadsUnseen ? 'more thread actions — other threads have new activity' : 'more thread actions'}
        title="more"
        data-other-threads-unseen={otherThreadsUnseen ? 'true' : undefined}
        onClick={toggle}
      >
        <MoreSvgIcon />
        {otherThreadsUnseen && (
          <ThreadUnreadBadge aria-hidden="true" data-unread="dot" $dot $placement="corner" />
        )}
      </HeaderIconButton>
      {open && typeof document !== 'undefined' && createPortal(
        <HeaderOverflowMenu
          ref={menuRef}
          role="menu"
          aria-label="thread actions"
          data-header-overflow-menu=""
          /* Body portal marker (portalClickGuard contract): without it,
             useGlobalChat's outside-click handler treats a tap on any menu
             item as "outside the chat" and closes the whole pane — on
             mobile (headerRailDensity) every ⋯ item did exactly that. */
          data-prest0n-portal="true"
          $left={pos.left}
          $top={pos.top}
        >
          {moveToOpen && showMoveTo ? (
            <CategoryMoveList
              categories={categories}
              currentCategoryId={currentCategoryId}
              naturalSectionId={naturalSectionId}
              lang={lang}
              onMoveToCategory={onMoveToCategory!}
              onCreateCategory={onCreateCategory}
              threadTitle={threadTitle}
              onDone={() => close(false)}
            />
          ) : null}
          {!moveToOpen && showPin && (
            <HeaderOverflowMenuItem
              type="button"
              role="menuitem"
              data-header-overflow-pin=""
              data-pinned={pinned ? 'true' : 'false'}
              aria-pressed={pinned}
              aria-label={pinned ? `unpin thread ${threadTitle}` : `pin thread ${threadTitle} to the top`}
              onClick={pick(() => onTogglePin(threadId!))}
            >
              <PinSvgIcon filled={pinned} />
              {pinned ? 'unpin thread' : 'pin thread'}
            </HeaderOverflowMenuItem>
          )}
          {!moveToOpen && showMoveTo && (
            <HeaderOverflowMenuItem
              type="button"
              role="menuitem"
              aria-haspopup="true"
              data-header-overflow-move-to=""
              aria-label={`move thread ${threadTitle} to a category`}
              onClick={(e) => {
                e.preventDefault();
                setMoveToOpen(true);
              }}
            >
              <CategoryFolderGlyph size={14} />
              move to…
            </HeaderOverflowMenuItem>
          )}
          {!moveToOpen && showArchive && (
            <HeaderOverflowMenuItem
              type="button"
              role="menuitem"
              data-header-overflow-archive=""
              aria-label={`archive thread ${threadTitle} (Prest0n keeps it in memory for reference; the conversation closes)`}
              onClick={pick(() => onRequestArchive(threadId!))}
            >
              <ArchiveBoxSvgIcon />
              archive thread
            </HeaderOverflowMenuItem>
          )}
          {!moveToOpen && addPeopleEnabled && (
            <HeaderOverflowMenuItem
              type="button"
              role="menuitem"
              data-header-overflow-add-people=""
              aria-label={canManagePeople === false ? 'see who is in this thread' : 'add people to this thread'}
              onClick={pick(onAddPeople)}
            >
              <AddPeopleSvgIcon />
              {canManagePeople === false ? 'people' : 'add people'}
            </HeaderOverflowMenuItem>
          )}
          {/* Prest0n in / out of this thread (2026-09-08): a permitted user's
              one-click way to make a thread human-only, and to bring Prest0n
              back. The state is in the label, same rule as the @ item below. */}
          {!moveToOpen && assistantToggleEnabled && canManagePeople !== false && onToggleAssistant && (
            <HeaderOverflowMenuItem
              type="button"
              role="menuitem"
              data-header-overflow-assistant={assistantEnabled === false ? 'out' : 'in'}
              aria-label={assistantEnabled === false ? 'add Prest0n back to this thread' : 'remove Prest0n from this thread'}
              onClick={pick(() => onToggleAssistant(assistantEnabled === false))}
            >
              <AddPeopleSvgIcon />
              {assistantEnabled === false ? 'add Prest0n back' : 'remove Prest0n from this thread'}
            </HeaderOverflowMenuItem>
          )}
          {!moveToOpen && summonToggleEnabled && (
            <HeaderOverflowMenuItem
              type="button"
              role="menuitemcheckbox"
              aria-checked={summonRequireMention !== false}
              data-header-overflow-summon-toggle={summonRequireMention !== false ? 'required' : 'always'}
              aria-label="require an @Prest0n mention for Prest0n to reply in this thread"
              onClick={pick(() => onToggleSummonRequirement?.(!(summonRequireMention !== false)))}
            >
              {/* Same state-in-the-icon rule as the wide rail: plain @ =
                  mention required, slashed @ = replies to everything. */}
              {summonRequireMention !== false ? <MentionSvgIcon /> : <MentionOffSvgIcon />}
              {summonRequireMention !== false
                ? 'Prest0n replies only when mentioned'
                : 'Prest0n replies to every message'}
            </HeaderOverflowMenuItem>
          )}
          {!moveToOpen && showNewThread && (
            <HeaderOverflowMenuItem
              type="button"
              role="menuitem"
              data-header-overflow-new-thread=""
              aria-label="start new thread"
              onClick={pick(() => onNewThread!())}
            >
              <PlusSvgIcon />
              new thread
            </HeaderOverflowMenuItem>
          )}
          {!moveToOpen && showClear && (
            <HeaderOverflowMenuItem
              type="button"
              role="menuitem"
              data-header-overflow-clear=""
              aria-label={clearAriaLabel}
              onClick={pick(onClear)}
            >
              <TrashSvgIcon />
              {clearLabel}
            </HeaderOverflowMenuItem>
          )}
        </HeaderOverflowMenu>,
        document.body,
      )}
    </>
  );
};

export default ThreadHeaderOverflowMenu;
